import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { sommelierApi } from '../api/sommelier'
import Boton from '../components/Boton'
import Paginador from '../components/Paginador'
import { fmtFecha, fmtHoraDe, fmtMonto, hoyISO, toISOLocal } from '../utils/fecha'
import { colors, text, space, sx, radius } from '../design/tokens'

// Solo presentación y lectura del contrato existente. El motor de IA y la
// atribución de ventas pertenecen a otros circuitos y no se modifican acá.
const SOLO = [
  { id: '', label: 'Todas las consultas' },
  { id: 'agregado', label: 'Con agregado al carrito' },
  { id: 'compra', label: 'Con compra registrada' },
]
const POR_PAGINA = 20
const fmtPlata = (n) => n == null ? '—' : `$ ${fmtMonto(n, 0)}`
const pct = (n, total) => total ? `${Math.round(n / total * 100)} %` : '—'

function periodo(dias = 30) {
  const hasta = hoyISO()
  return { desde: toISOLocal(new Date(new Date(`${hasta}T12:00:00-03:00`).getTime() - (dias - 1) * 86400000)), hasta }
}

function Fecha({ valor }) {
  return <span style={st.fecha}>{fmtFecha(valor)}<span style={st.hora}>{fmtHoraDe(valor)}</span></span>
}

function Valoracion({ n, title }) {
  if (!n) return null
  return (
    <span style={{ ...sx.mono, whiteSpace: 'nowrap', fontSize: text.sec }} title={title || `El cliente puntuó la respuesta con ${n} de 5`} aria-label={`${n} de 5`}>
      <span style={{ color: '#2D6A3F', letterSpacing: 1 }}>{'★'.repeat(n)}</span>
      <span style={{ color: colors.border, letterSpacing: 1 }}>{'★'.repeat(5 - n)}</span>
    </span>
  )
}

function Estado({ consulta }) {
  const compra = consulta.compra
  const label = compra ? (compra.con_sugeridos ? 'Compra con sugeridos' : 'Compra registrada')
    : consulta.agregado_at ? 'Agregado al carrito' : 'Consulta registrada'
  const tone = compra ? { background: colors.okBg, color: colors.ok }
    : consulta.agregado_at ? { background: colors.infoBg, color: colors.info }
      : { background: colors.bgSoft, color: colors.textSub }
  return <span style={{ ...st.badge, ...tone }}>{label}</span>
}

// La pestaña Lecciones se sacó el 1 oct 2026: Martin entrena a los agentes
// por chat con Claude (memoria en agente_memorias), sin pantallas de reglas.
export default function SommelierConsultasPage() {
  return (
    <div style={st.page}>
      <ConsultasVista />
    </div>
  )
}

function ConsultasVista() {
  const [filtros, setFiltros] = useState(() => ({ ...periodo(), solo: '', q: '' }))
  const [borrador, setBorrador] = useState(filtros)
  const [pagina, setPagina] = useState(1)
  const invalido = !borrador.desde || !borrador.hasta || borrador.desde > borrador.hasta
  const pendientes = Object.keys(filtros).some(key => filtros[key] !== borrador[key])
  // Fetch propio: la app NO monta QueryClientProvider (react-query está en
  // package.json pero ninguna pantalla lo usa) — con useQuery esta pantalla
  // rompía en producción ("No QueryClient set", 12/09). Misma forma de datos.
  const [data, setData] = useState(null)
  const [isFetching, setIsFetching] = useState(true)
  const [isError, setIsError] = useState(false)
  const isPending = isFetching && !data
  const refetch = useCallback(async () => {
    setIsFetching(true); setIsError(false)
    try {
      const r = await sommelierApi.consultas({ ...filtros, q: filtros.q || undefined, solo: filtros.solo || undefined })
      setData(r.data)
    } catch {
      setIsError(true)
    } finally { setIsFetching(false) }
  }, [filtros])
  useEffect(() => { refetch() }, [refetch])
  const filas = data?.data ?? []
  const k = data?.kpis
  const totalPaginas = Math.max(1, Math.ceil(filas.length / POR_PAGINA))
  const actual = Math.min(pagina, totalPaginas)
  const visibles = filas.slice((actual - 1) * POR_PAGINA, actual * POR_PAGINA)

  function aplicar(event) {
    event.preventDefault()
    if (invalido) return
    setFiltros({ ...borrador, q: borrador.q.trim() })
    setBorrador({ ...borrador, q: borrador.q.trim() })
    setPagina(1)
  }

  function limpiar() {
    const defaults = { ...periodo(), solo: '', q: '' }
    setBorrador(defaults)
    setFiltros(defaults)
    setPagina(1)
  }

  return (
    <>
      <header style={st.header}>
        <div style={st.actions}><h1 style={st.title}>Sommelier IA</h1><span style={st.meta}>{fmtFecha(filtros.desde)} — {fmtFecha(filtros.hasta)}</span></div>
        <Boton type="button" sm disabled={isFetching} onClick={() => refetch()}>{isFetching ? 'Actualizando…' : '↻ Actualizar'}</Boton>
      </header>

      <section aria-label="Resumen del período" aria-busy={isFetching}>
        <div style={st.kpis}>
          <Kpi label="Consultas" valor={k?.consultas} />
          <Kpi label="Al carrito" valor={k?.con_agregado} sub={k ? pct(k.con_agregado, k.consultas) : '—'} />
          <Kpi label="Con compra" valor={k?.con_compra} sub={k ? pct(k.con_compra, k.consultas) : '—'} />
          <Kpi label="Compraron sugeridos" valor={k?.con_compra_sugeridos} sub={k ? pct(k.con_compra_sugeridos, k.consultas) : '—'} destacado />
          <Kpi label="Importe asociado*" valor={k ? fmtPlata(k.plata) : '—'} />
          <Kpi label="Valoración del cliente" valor={k?.valoracion_promedio != null ? `${String(k.valoracion_promedio).replace('.', ',')} / 5` : '—'} sub={k?.valoraciones ? `${k.valoraciones} ${k.valoraciones === 1 ? 'respuesta puntuada' : 'respuestas puntuadas'}` : 'sin puntuaciones'} />
        </div>
        <details style={st.metricHelp}><summary style={{ cursor: 'pointer' }}>Cómo se miden estos resultados</summary><p style={st.note}>Los indicadores corresponden a todo el período; búsqueda y resultado filtran solo el listado. Una conversación agrupa la pregunta y sus ajustes; se cuentan conversaciones, no turnos ni clientes. *El importe asociado puede sumar un mismo pedido en varias consultas.</p></details>
      </section>

      <form style={st.filters} onSubmit={aplicar} aria-label="Filtrar consultas">
        <div style={st.fields}>
          <input aria-label="Buscar en preguntas, respuestas o email" type="search" value={borrador.q} onChange={e => setBorrador({ ...borrador, q: e.target.value })} placeholder="Buscar pregunta, respuesta o email…" style={{ ...st.input, flex: '2 1 220px', width: 'auto' }} />
          <input aria-label="Desde" type="date" required value={borrador.desde} max={borrador.hasta || undefined} onChange={e => setBorrador({ ...borrador, desde: e.target.value })} style={{ ...st.input, flex: '1 1 125px', width: 'auto' }} />
          <input aria-label="Hasta" type="date" required value={borrador.hasta} min={borrador.desde || undefined} onChange={e => setBorrador({ ...borrador, hasta: e.target.value })} style={{ ...st.input, flex: '1 1 125px', width: 'auto' }} />
          <select aria-label="Período rápido" value="" onChange={e => setBorrador({ ...borrador, ...periodo(Number(e.target.value)) })} style={{ ...st.input, flex: '0 1 105px', width: 'auto' }}><option value="" disabled>Período</option><option value="1">Hoy</option><option value="7">7 días</option><option value="30">30 días</option></select>
          <select aria-label="Resultado" value={borrador.solo} onChange={e => setBorrador({ ...borrador, solo: e.target.value })} style={{ ...st.input, flex: '1 1 170px', width: 'auto' }}>{SOLO.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}</select>
          <div style={st.actions}><Boton type="submit" variante="primary" disabled={invalido}>Aplicar</Boton><Boton type="button" variante="ghost" onClick={limpiar}>Limpiar</Boton></div>
        </div>
        {(invalido || pendientes) && <span style={{ ...st.meta, color: invalido ? colors.err : colors.textSec }} role="status">{invalido ? 'Revisá el rango de fechas.' : 'Filtros pendientes de aplicar.'}</span>}
      </form>

      {isError && <div role="alert" style={st.errorBox}><div><strong>No se pudieron actualizar las consultas.</strong><div>{data ? 'Los datos visibles son de la última carga correcta.' : 'Probá nuevamente en unos instantes.'}</div></div><Boton type="button" disabled={isFetching} onClick={() => refetch()}>Reintentar</Boton></div>}

      <section aria-label="Historial de consultas" aria-busy={isFetching}>
        <div style={st.listHeader}>
          <div style={st.actions}><h2 style={st.sectionTitle}>Historial de consultas</h2>{data && <span style={st.count}>{filas.length}{filas.length >= 500 ? '+' : ''}</span>}</div>
          <span role="status" style={st.meta}>{isFetching ? 'Cargando consultas…' : 'Abrí una consulta para ver el detalle'}</span>
        </div>
        {isPending && !isError ? <div style={st.empty} role="status">Cargando preguntas, respuestas y resultados…</div>
          : data && filas.length === 0 ? <div style={st.empty}><h3 style={st.sectionTitle}>No hay consultas para estos filtros</h3><p>Probá otro período o limpiá la búsqueda para ampliar los resultados.</p><Boton type="button" onClick={limpiar}>Limpiar filtros</Boton></div>
            : <div style={st.list}>{visibles.map(c => <Consulta key={c.id} consulta={c} />)}</div>}
        {filas.length >= 500 && <p style={st.note}>El servidor devuelve hasta 500 consultas recientes. Acotá el período o la búsqueda para revisar otras consultas.</p>}
        {data && filas.length > 0 && <div style={st.pagination}><Paginador pagina={actual} totalPaginas={totalPaginas} onCambiar={setPagina} info={`Mostrando ${(actual - 1) * POR_PAGINA + 1}–${Math.min(actual * POR_PAGINA, filas.length)} de ${filas.length} consultas cargadas`} /></div>}
      </section>
      <p style={st.note}>Sin registro de carrito o compra no significa abandono: las consultas antiguas pueden no tener seguimiento. Una compra vinculada tampoco implica que se haya comprado todo lo recomendado.</p>
    </>
  )
}

function Kpi({ label, valor, sub, destacado }) {
  return <div style={{ ...st.kpi, ...(destacado ? { borderColor: colors.ok, background: colors.okSoft } : {}) }}>
    <div style={st.kpiLabel}>{label}</div>
    <div style={{ ...st.kpiValue, color: destacado ? colors.ok : colors.text }}>{valor ?? '—'}</div>
    {sub && <span style={st.kpiPercent} title="Porcentaje de las consultas del período">{sub}</span>}
  </div>
}

function Consulta({ consulta: c }) {
  // Una fila = una CONVERSACIÓN (Martin, 12 sep 2026): la pregunta inicial y
  // todos sus ajustes ("1 de cada uno", "más barato"…) juntos, en orden.
  const sugeridos = c.sugeridos ?? []
  const turnos = c.turnos ?? []
  const agregados = sugeridos.filter(s => s.agregado).length
  return (
    <details style={st.consulta}>
      <summary style={st.summary}>
        <div style={st.summaryBody}>
          <div style={st.summaryDate}><Fecha valor={c.fecha} /><span style={st.origin}>{c.origen === 'reemplazo' ? 'Reemplazo' : c.origen === 'chat' ? 'Chat' : c.origen || 'Sin origen'}</span>{turnos.length > 1 && <span style={st.origin}>{turnos.length} turnos</span>}</div>
          <div style={st.questionPreview}>{c.consulta || 'Pregunta no registrada'}</div>
          <div style={st.summaryResult}><Estado consulta={c} /><Valoracion n={c.valoracion} /><span style={st.meta}>{sugeridos.length} sugeridos{agregados > 0 ? ` · ${agregados} al carrito` : ''}</span></div>
        </div>
      </summary>
      <div style={st.detail}>
        <div style={st.conversation}>
          <div style={st.meta}>Cliente: {c.email || 'sin email registrado'}</div>
          {turnos.map((t, i) => (
            <div key={t.id} style={st.turno}>
              <section><h3 style={sx.cardTitle}>{i === 0 ? 'Pregunta del cliente' : `Ajuste ${i}`} <span style={st.turnoHora}>{fmtHoraDe(t.fecha)}</span></h3><p style={st.fullText}>{t.consulta || 'No se registró el texto de la pregunta.'}</p></section>
              <section style={st.answer}><h3 style={sx.cardTitle}>Respuesta del Sommelier {t.valoracion ? <Valoracion n={t.valoracion} /> : <span style={{ ...st.turnoHora }}>sin puntuar</span>}</h3><p style={st.fullText}>{t.respuesta_texto || 'No se registró el texto de la respuesta.'}</p>
                {t.sugeridos?.length > 0 && <ul style={st.products}>{t.sugeridos.map((s, j) => <li key={`${s.sku}-${j}`} style={st.product}><div style={st.productName}>{s.nombre || s.sku}<div style={st.sku}>{s.sku}</div></div><span style={{ ...st.badge, background: s.agregado ? colors.okBg : colors.bgSoft, color: s.agregado ? colors.ok : colors.textSub }}>{s.agregado ? 'Agregado al carrito' : 'Sugerido'}</span></li>)}</ul>}
              </section>
            </div>
          ))}
        </div>
        <aside style={st.journey} aria-label="Recorrido de la conversación">
          <h3 style={sx.cardTitle}>Recorrido de la conversación</h3>
          <ol style={st.steps}>
            <Paso numero="1" titulo={turnos.length > 1 ? `Conversación de ${turnos.length} turnos` : 'Consulta recibida'} fecha={c.fecha}>{turnos.length > 1 ? `Empezó ${fmtFecha(c.fecha)} y terminó ${fmtHoraDe(c.ultima)}.` : 'Se guardó la consulta del cliente.'}</Paso>
            <Paso numero="2" titulo="Agregado al carrito" fecha={c.agregado_at} pendiente={!c.agregado_at}>{c.agregado_at ? 'Se registró un agregado desde esta propuesta.' : 'Sin agregado registrado.'}</Paso>
            <Paso numero="3" titulo="Compra registrada" fecha={c.compra?.at} pendiente={!c.compra}>{c.compra ? <div style={st.purchase}>{c.compra.nota_venta_id ? <Link to={`/notas-venta/${c.compra.nota_venta_id}`} style={st.link}>{c.compra.numero || 'Ver nota de venta'} ↗</Link> : <strong>{c.compra.numero || 'Sin número registrado'}</strong>}<div style={st.amount}>{fmtPlata(c.compra.total)}</div><div>{c.compra.con_sugeridos ? 'Incluye al menos un producto sugerido.' : 'No se registró compra de productos sugeridos.'}</div></div> : 'Sin compra registrada.'}</Paso>
          </ol>
        </aside>
      </div>
    </details>
  )
}

function Paso({ numero, titulo, fecha, pendiente, children }) {
  return <li style={st.step}><span style={{ ...st.stepDot, background: pendiente ? colors.bgSoft : colors.okBg, color: pendiente ? colors.textSec : colors.ok }}>{numero}</span><div style={st.stepContent}><h4 style={st.stepTitle}>{titulo}</h4>{fecha && <Fecha valor={fecha} />}<div style={st.stepText}>{children}</div></div></li>
}

const st = {
  page: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: space.lg, minWidth: 0, color: colors.text, fontSize: text.body },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.lg },
  title: { margin: 0, fontSize: 22, fontWeight: 650, letterSpacing: '-0.025em' },
  sub: { margin: 0, color: colors.textSec, lineHeight: 1.6 },
  metricHelp: { color: colors.textSec, fontSize: text.meta, marginTop: 6, lineHeight: 1.6 },
  kpiPercent: { color: colors.textSec, fontSize: text.sec },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.md },
  sectionTitle: { margin: 0, fontSize: text.title, fontWeight: 600 },
  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 155px), 1fr))', gap: space.lg },
  kpi: { ...sx.card, padding: '10px 12px', minWidth: 0, display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 8px' },
  kpiLabel: { width: '100%', display: 'flex', alignItems: 'center', gap: space.md, fontSize: text.sec, color: colors.textSub, fontWeight: 600 },
  stepNumber: { ...sx.mono, color: colors.textSec, fontSize: text.meta },
  kpiValue: { fontSize: 22, fontWeight: 650, fontVariantNumeric: 'tabular-nums', margin: 0, overflowWrap: 'anywhere' },
  meta: { color: colors.textSec, fontSize: text.sec, lineHeight: 1.5 },
  note: { margin: 0, color: colors.textSec, fontSize: text.sec, lineHeight: 1.6 },
  filters: { ...sx.card, padding: space.lg, display: 'grid', gap: space.md },
  fields: { display: 'flex', flexWrap: 'wrap', gap: space.lg, alignItems: 'end' },
  field: { display: 'flex', flexDirection: 'column', gap: space.sm, flex: '0 1 135px', minWidth: 0, fontSize: text.sec, fontWeight: 600, color: colors.textSub },
  input: { ...sx.input, width: '100%', minWidth: 0, boxSizing: 'border-box', height: 34, color: colors.text, background: colors.bg },
  filterFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: space.lg },
  actions: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  errorBox: { background: colors.errBg, color: colors.err, padding: space.xl, borderRadius: radius.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.lg, lineHeight: 1.6 },
  listHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.lg, flexWrap: 'wrap', marginBottom: space.lg },
  count: { ...sx.badge, background: colors.bgSoft, color: colors.textSub, border: `1px solid ${colors.border}` },
  list: { display: 'grid', gap: space.xs },
  turno: { display: 'grid', gap: space.md, paddingTop: space.md, borderTop: `1px dashed ${colors.border}` },
  turnoHora: { ...sx.mono, fontWeight: 400, color: colors.textSub, marginLeft: 6 },
  pagination: { overflowX: 'auto', paddingBottom: space.xs },
  empty: { ...sx.card, textAlign: 'center', padding: '40px 20px', color: colors.textSec, lineHeight: 1.6 },
  consulta: { ...sx.card, padding: 0, minWidth: 0 },
  summary: { padding: '10px 12px', cursor: 'pointer', color: colors.textSub },
  summaryDate: { flex: '0 0 145px', display: 'grid', gap: 3, justifyItems: 'start' },
  summaryResult: { flex: '0 0 180px', display: 'grid', gap: 3, justifyItems: 'start' },
  summaryBody: { display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 14px', width: 'calc(100% - 22px)', verticalAlign: 'middle', marginLeft: space.xs },
  rowHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  origin: { ...sx.badge, background: colors.bgSoft, color: colors.textSub, border: `1px solid ${colors.border}` },
  badge: { display: 'inline-block', borderRadius: radius.pill, padding: '4px 10px', fontSize: text.meta, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'normal' },
  fecha: { ...sx.mono, display: 'inline-flex', flexWrap: 'wrap', gap: space.md, color: colors.textSec, fontSize: text.meta, lineHeight: 1.5 },
  hora: { color: colors.textSub },
  questionPreview: { flex: '1 1 220px', minWidth: 0, color: colors.text, fontSize: text.body, fontWeight: 500, lineHeight: 1.5, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  preview: { color: colors.textSec, lineHeight: 1.6, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  rowFooter: { marginTop: space.lg, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: space.lg },
  email: { color: colors.textSec, fontSize: text.sec, overflowWrap: 'anywhere', minWidth: 0 },
  detailHint: { marginLeft: 'auto', color: colors.link, fontWeight: 600, fontSize: text.sec },
  detail: { display: 'flex', flexWrap: 'wrap', gap: space['3xl'], padding: space['2xl'], borderTop: `1px solid ${colors.border}`, background: colors.bgSoft, borderRadius: `0 0 ${radius.lg}px ${radius.lg}px` },
  conversation: { flex: '2 1 340px', minWidth: 0, display: 'grid', gap: space['2xl'] },
  fullText: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.75, margin: '10px 0 0', color: colors.text },
  answer: { ...sx.card },
  products: { listStyle: 'none', padding: 0, margin: '10px 0 0', display: 'grid', gap: space.sm },
  product: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: space.md, padding: '10px 0', borderBottom: `1px solid ${colors.border}` },
  productName: { flex: '1 1 180px', fontWeight: 500, overflowWrap: 'anywhere' },
  sku: { ...sx.mono, fontSize: text.meta, color: colors.textSec, marginTop: space.xs },
  journey: { ...sx.card, flex: '1 1 230px', minWidth: 0, alignSelf: 'flex-start' },
  steps: { listStyle: 'none', padding: 0, margin: '20px 0 0' },
  step: { display: 'flex', gap: space.lg, paddingBottom: space['2xl'] },
  stepDot: { display: 'grid', placeItems: 'center', width: 26, height: 26, flexShrink: 0, borderRadius: radius.pill, fontSize: text.sec, fontWeight: 600 },
  stepContent: { minWidth: 0 },
  stepTitle: { fontSize: text.body, fontWeight: 600, margin: '4px 0 6px' },
  stepText: { color: colors.textSec, fontSize: text.sec, lineHeight: 1.6, marginTop: space.sm, overflowWrap: 'anywhere' },
  purchase: { display: 'grid', gap: space.sm },
  amount: { ...sx.mono, fontSize: text.title, color: colors.text, fontWeight: 600 },
  link: { color: colors.link, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 },
  vistas: { display: 'flex', gap: space.sm, flexWrap: 'wrap' },
  leccion: { ...sx.card, display: 'grid', gap: space.md, minWidth: 0 },
  leccionCabecera: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 12px' },
  leccionTexto: { margin: 0, fontSize: text.body, lineHeight: 1.7, overflowWrap: 'anywhere', color: colors.text },
  evidencia: { color: colors.textSec, fontSize: text.sec, lineHeight: 1.6 },
  uids: { ...sx.mono, margin: '8px 0 0', fontSize: text.meta, color: colors.textSub, overflowWrap: 'anywhere' },
  ejemplo: { display: 'grid', gap: space.xs, marginTop: space.md, paddingTop: space.md, borderTop: `1px dashed ${colors.border}` },
  ejemploRol: { ...sx.mono, display: 'inline-block', minWidth: 78, fontSize: text.meta, color: colors.textSub, textTransform: 'uppercase', letterSpacing: '0.05em' },
  grupoCerrado: { color: colors.textSec, fontSize: text.sec },
  grupoCerradoTitulo: { cursor: 'pointer', fontSize: text.title, fontWeight: 600, color: colors.text, display: 'flex', alignItems: 'center', gap: space.md, marginBottom: space.lg },
}
